"use strict";

//https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/examples/dialog/
const baseTemplate = document.createElement("template");
baseTemplate.innerHTML = require("./template");
const typography = require('!!css-loader?{"sourceMap":false}!postcss-loader!../../tokens/typography.css').default;

/**
 * Same breakpoint as style/index.css: below it the dialog is a full-height
 * sheet, the page behind it is locked and the host follows the visual viewport.
 */
const SHEET_MEDIA_QUERY = "(max-width: 768px)";

function isSheetViewport() {
    return typeof window.matchMedia === "function" && window.matchMedia(SHEET_MEDIA_QUERY).matches;
}

class Element extends HTMLElement {

    constructor(template) {
        super();

        this.root = this.attachShadow({ mode: "open" });

        /**
         * Applying styles inside the shadow DOM:
         * @see https://developer.mozilla.org/en-US/docs/Web/API/Web_components/Using_shadow_DOM#applying_styles_inside_the_shadow_dom
         */
        const sheet = new CSSStyleSheet();
        sheet.replaceSync(typography);
        this.root.adoptedStyleSheets = [sheet];


        const content = template ? template.content.cloneNode(true) : baseTemplate.content.cloneNode(true);
        this.root.appendChild(content);

        this._dialog = this.shadowRoot.querySelector('.swiftref-dialog');
        this._dialogContent = this._dialog.querySelector('.dialog-content');


        /**
         * Handles keyboard events for the modal
         * - Escape key closes the modal
         * - Tab key manages focus trap
         * @param {KeyboardEvent} event
         */
        this.handleKeydown = function (event) {
            // Only handle events when modal is open
            if (!this.hasAttribute('open')) return;


            if (event.key === 'Escape') {
                event.preventDefault();
                this.hide();
            } else if (event.key === 'Tab') {
                this._trapTab(event);
            }
        }.bind(this);

        /**
         * Sizes the host to the VISUAL viewport while the sheet is open: on iOS
         * the layout viewport does not shrink for the software keyboard, so a
         * 100%-high host would keep its footer behind the keyboard.
         */
        this._followVisualViewport = function () {
            const viewport = window.visualViewport;
            if (!viewport) return;
            this.style.height = `${viewport.height}px`;
            this.style.top = `${viewport.offsetTop}px`;
        }.bind(this);
        this._pageLock = null;
        // the element that had the focus when the dialog opened: it gets it back on close
        this._opener = null;

        /**
         * Opt-in (`backdrop_close`): a tap on the overlay outside the box closes
         * the dialog. Off by default — a form dialog that loses what was typed
         * on a stray tap is worse than one that does not close.
         */
        this._onBackdropClick = function (event) {
            if (this.hasAttribute("backdrop_close") && event.composedPath()[0] === this) {
                this.hide();
            }
        }.bind(this);

    }

    /**
     * Phone sheet only: stop the page behind from scrolling and follow the
     * visual viewport. A no-op above the breakpoint, so desktop consumers keep
     * their behaviour.
     */
    _lockPage() {
        if (this._pageLock || !isSheetViewport()) return;
        this._pageLock = {
            html: document.documentElement.style.overflow,
            body: document.body.style.overflow
        };
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        if (window.visualViewport) {
            window.visualViewport.addEventListener("resize", this._followVisualViewport);
            window.visualViewport.addEventListener("scroll", this._followVisualViewport);
            this._followVisualViewport();
        }
    }

    _unlockPage() {
        if (!this._pageLock) return;
        document.documentElement.style.overflow = this._pageLock.html;
        document.body.style.overflow = this._pageLock.body;
        this._pageLock = null;
        if (window.visualViewport) {
            window.visualViewport.removeEventListener("resize", this._followVisualViewport);
            window.visualViewport.removeEventListener("scroll", this._followVisualViewport);
        }
        this.style.removeProperty("height");
        this.style.removeProperty("top");
    }

    /**
     * Tab stays inside the open dialog: the close button is the first stop and
     * the submit button the last; everything slotted between them tabs
     * naturally. The focus is read at the document scope, where a focused
     * control inside a shadow root shows as its host.
     */
    _trapTab(event) {
        const first = this.hideButton;
        // the submit button is usually SLOTTED (light DOM); the shadow one is the default
        const last = this.querySelector('[slot="submit"]') || this.shadowRoot.querySelector('#submit-button');
        if (!first || !last) return;
        const inShadow = this.shadowRoot.activeElement;
        const active = document.activeElement;
        const inside = inShadow !== null || this.contains(active);
        if (!inside) {
            event.preventDefault();
            first.focus();
        } else if (event.shiftKey && inShadow === first) {
            event.preventDefault();
            if (typeof last.focus === "function") last.focus();
        } else if (!event.shiftKey && (active === last || inShadow === last)) {
            event.preventDefault();
            first.focus();
        }
    }

    /**
     * On open the focus moves into the dialog: to the first field on a desktop,
     * to the box itself on a phone sheet, where focusing a field would raise
     * the keyboard over the form before the user has read it.
     */
    _focusOnOpen() {
        if (isSheetViewport()) {
            this._dialog.focus();
            return;
        }
        const field = this.querySelector("swiftref-input, swiftref-dropdown, input, select, textarea");
        if (field && typeof field.focus === "function") {
            field.focus();
        } else if (this.hideButton) {
            this.hideButton.focus();
        }
    }

    _restoreFocus() {
        const opener = this._opener;
        this._opener = null;
        // The page may have moved the focus on close (to a message it scrolled
        // into view): returning it to the opener would scroll back there.
        const active = document.activeElement;
        if (active && active !== document.body && active !== this && !this.contains(active)) {
            return;
        }
        if (opener && opener !== document.body && opener.isConnected && typeof opener.focus === "function") {
            opener.focus();
        }
    }



    get open() {
        return this.hasAttribute("open");
    }
    set open(value) {
        this.setAttribute("open", "");
    }
    get submit_label() {
        return this.getAttribute("submit_label");
    }
    set submit_label(value) {
        this.setAttribute("submit_label", value);
    }


    static get observedAttributes() {
        return ["name", "open", "submit_label", "title_icon"];
    }

    attributeChangedCallback(name, oldValue, newValue) {

        switch (name) {
            case "name":
                this.shadowRoot.querySelector('#dialog-title').textContent = newValue;
                this._dialog.setAttribute("aria-labelledby", newValue);
                break;
            case "submit_label":
                try {
                    this._dialog.querySelector("#submit-button").setAttribute("label", newValue);
                } catch (e) {}
                break;
            case "open":
                // the attribute is the single source of "open" (show(), the
                // setter or a direct setAttribute all land here)
                if (this.open) {
                    this._opener = document.activeElement;
                    this._lockPage();
                } else {
                    this._unlockPage();
                }
                setTimeout(function () {
                    if (this.open) {
                        this._dialog.classList.add("animate");
                        document.addEventListener('keydown', this.handleKeydown);
                        this._focusOnOpen();
                    } else {
                        this._dialog.classList.remove("animate");
                        document.removeEventListener('keydown', this.handleKeydown);
                    }
                }.bind(this), 10);

                break;
            case "title_icon":
                this._dialog.querySelector(".title-icon").setAttribute("kind", newValue);
                break;
            default:
                break;
        }
    }


    connectedCallback() {
        this.addEventListener("click", this._onBackdropClick);
        // Setup event listeners for buttons
        this.hideButton = this.shadowRoot.querySelector('.close-icon');
        if (this.hideButton) {
            this.hideButton.addEventListener('click', this.hide.bind(this));
        }
        this.cancelButton = this.shadowRoot.querySelector('[slot="cancel"]') || this.shadowRoot.querySelector('swiftref-button[kind="secondary"]');
        if (this.cancelButton) {
            this.cancelButton.addEventListener('click', this.hide.bind(this));
        }
        this.submitButton = this.shadowRoot.querySelector('[slot="submit"]') || this.shadowRoot.querySelector('#submit-button');
        if (this.submitButton) {
            this.submitButton.addEventListener("click", this.submit.bind(this));
        }

        if (this.getAttribute("title_icon")) {
            this._dialog.querySelector(".title-icon").setAttribute("kind", this.getAttribute("title_icon"));
        }

    }
    /**
     * Lifecycle callback when element is removed from the DOM
     * Cleans up event listeners to prevent memory leaks
     */
    disconnectedCallback() {
        this._unlockPage();
        this.removeEventListener("click", this._onBackdropClick);
        document.removeEventListener('keydown', this.handleKeydown);
        if (this.hideButton) {
            this.hideButton.removeEventListener('click', this.hide.bind(this));
        }
        if (this.cancelButton) {
            this.cancelButton.removeEventListener('click', this.hide.bind(this));
        }
        if (this.submitButton) {
            this.submitButton.removeEventListener("click", this.submit.bind(this));
        }
    }



    submit() {
        this.dispatchEvent(new CustomEvent("submit", {
            detail: {},
            // bubbles: true
            // composed: true
        }))
    }

    /**
     * Public method to open the modal
     */
    show() {
        this.setAttribute('open', '');
        this.scrollTop();
    }


    scrollTop() {
        this.previousScrollTop = this._dialogContent.scrollTop;
        this._dialogContent.scrollTop = 0;
    }
    scrollDown() {
        this.previousScrollTop = this._dialogContent.scrollTop;
        this._dialogContent.scrollTop = this._dialogContent.scrollHeight;
    }
    resetScroll() {
        this._dialogContent.scrollTop = this.previousScrollTop;
    }

    /**
     * Public method to close the modal: "hide" fires at once, "hidden" once the
     * close transition is over and the dialog has released the page (the phone
     * sheet locks the page scroll while `open`), which is when the page can
     * scroll again.
     */
    hide() {

        if (this.open) {
            this.dispatchEvent(new CustomEvent("hide", {
                detail: {},
                bubbles: true
                // composed: true
            }))
            this._dialog.classList.remove("animate");
            setTimeout(function () {
                this.removeAttribute('open');
                this._restoreFocus();
                this.dispatchEvent(new CustomEvent("hidden", {
                    detail: {},
                    bubbles: true
                }));
            }.bind(this), 200);
            document.removeEventListener('keydown', this.handleKeydown);
        }

    }

}


window.customElements.define("swiftref-dialog", Element);

module.exports = Element;
